import Dexie, { type EntityTable } from 'dexie';
import type { LearningSession } from './learning';
import type { ProgramRecord } from '@/domain/program/types';
import type { JourneyRecord } from '@/domain/journey/model';
import type { EventRecord, ParticipantRecord, ProtocolRecord, ScoreRecord, SessionRecord, TrialRecord } from './types';

/**
 * IndexedDB 스키마 (Dexie).
 *
 * 인덱스만 선언한다 — 레코드 전체는 그대로 저장된다.
 * 트라이얼 실행 중에는 이 모듈을 건드리지 않는다. 블록 종료 시 bulkAdd 로만 쓴다.
 */
export class CbmiDb extends Dexie {
  journeys!: EntityTable<JourneyRecord, 'id'>;
  programs!: EntityTable<ProgramRecord, 'id'>;
  learningSessions!: EntityTable<LearningSession, 'id'>;
  participants!: EntityTable<ParticipantRecord, 'id'>;
  sessions!: EntityTable<SessionRecord, 'id'>;
  trials!: EntityTable<TrialRecord, 'pk'>;
  events!: EntityTable<EventRecord, 'pk'>;
  scores!: EntityTable<ScoreRecord, 'id'>;
  protocols!: EntityTable<ProtocolRecord, 'id'>;

  constructor(name = 'cbmi') {
    super(name);
    this.version(1).stores({
      participants: 'id, &code, createdAt',
      sessions: 'id, participantId, arm, startedAt, [participantId+startedAt]',
      trials: '++pk, sessionId, [sessionId+trialIndex]',
      events: '++pk, sessionId, ts',
      scores: 'id, sessionId, participantId, computedAt, [participantId+computedAt]',
    });
    // v2: 프로토콜 등록. 새 테이블만 추가 — 기존 데이터 마이그레이션 없음.
    this.version(2).stores({
      participants: 'id, &code, createdAt',
      sessions: 'id, participantId, arm, startedAt, [participantId+startedAt]',
      trials: '++pk, sessionId, [sessionId+trialIndex]',
      events: '++pk, sessionId, ts',
      scores: 'id, sessionId, participantId, computedAt, [participantId+computedAt]',
      protocols: 'id, &key, createdAt',
    });
    this.version(3).stores({
      learningSessions: 'id, participantCode, mode, episodeId, startedAt, status',
    });
    this.version(4).stores({ programs: 'id, participantCode, contentVersion, updatedAt' });
    this.version(5).stores({ journeys: 'id, participantCode, contentVersion, updatedAt' });
  }
}

export const db = new CbmiDb();

/** 저장 지속성 요청. iOS Safari 는 미사용 시 데이터를 지울 수 있다. */
export async function requestPersistence(): Promise<boolean> {
  try { return (await navigator.storage?.persist?.()) ?? false; } catch { return false; }
}
